import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, Search, Trash2, Sparkles, Loader2, 
  ArrowRight, Lightbulb
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

export default function PlayHome({ 
  playIdeas, 
  setPlayIdeas, 
  onOpenIdea,
  onRefresh 
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isNewIdeaDialogOpen, setIsNewIdeaDialogOpen] = useState(false);
  const [newIdeaText, setNewIdeaText] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const filteredIdeas = playIdeas.filter(idea => {
    if (!searchQuery) return true;
    return idea.root_premise?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleCreateIdea = async () => {
    if (!newIdeaText.trim()) return;
    
    setIsCreating(true);
    try {
      const response = await fetch(`${API}/play/ideas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ root_premise: newIdeaText.trim() })
      });
      
      if (response.ok) {
        const newIdea = await response.json();
        setPlayIdeas([newIdea, ...playIdeas]);
        setNewIdeaText("");
        setIsNewIdeaDialogOpen(false);
        // Open the new idea immediately
        onOpenIdea(newIdea);
        toast.success("Idea created!");
      } else {
        toast.error("Failed to create idea");
      }
    } catch (error) {
      toast.error("Failed to create idea");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteIdea = async (ideaId, e) => {
    e.stopPropagation();
    
    if (!window.confirm("Delete this idea? This cannot be undone.")) {
      return;
    }
    
    setDeletingId(ideaId);
    try {
      const response = await fetch(`${API}/play/ideas/${ideaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setPlayIdeas(playIdeas.filter(i => i.idea_id !== ideaId));
        toast.success("Idea deleted");
      } else {
        toast.error("Failed to delete idea");
      }
    } catch (error) {
      toast.error("Failed to delete idea");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full p-4 md:p-6" data-testid="play-home">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="font-display text-2xl uppercase tracking-wider mb-2">
          Idea Playground
        </h2>
        <p className="text-muted-foreground text-sm">
          Pick an idea and mess with it.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-6 max-w-2xl mx-auto w-full">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search ideas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-muted border-0 text-sm"
            data-testid="play-search"
          />
        </div>
        <Button 
          onClick={() => setIsNewIdeaDialogOpen(true)}
          className="bg-primary text-primary-foreground"
          data-testid="new-idea-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Idea
        </Button>
      </div>

      {/* Ideas Grid */}
      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto">
          {filteredIdeas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Sparkles className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="font-display text-xl uppercase mb-2">
                {searchQuery ? "No matching ideas" : "No ideas in your playground yet"}
              </h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-md">
                {searchQuery 
                  ? "Try a different search term."
                  : "Send one from Capture or create a new one."
                }
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => setIsNewIdeaDialogOpen(true)}
                  className="bg-primary text-primary-foreground"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Idea
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 pb-4">
              {filteredIdeas.map((idea) => (
                <div
                  key={idea.idea_id}
                  className="group bg-card border border-border rounded-sm hover:border-primary/50 transition-all cursor-pointer"
                  onClick={() => onOpenIdea(idea)}
                  data-testid={`play-idea-card-${idea.idea_id}`}
                >
                  {/* Card Content - Scrollable */}
                  <div className="p-4 max-h-48 overflow-y-auto">
                    <p className="font-mono text-sm whitespace-pre-wrap">
                      {idea.root_premise}
                    </p>
                  </div>
                  
                  {/* Card Footer */}
                  <div className="flex items-center justify-between p-3 border-t border-border bg-muted/30">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {idea.source === "capture" && (
                        <span className="flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" />
                          from Capture
                        </span>
                      )}
                      {idea.angles?.length > 0 && (
                        <span>{idea.angles.length} angle{idea.angles.length !== 1 ? 's' : ''}</span>
                      )}
                      {idea.good_lines?.length > 0 && (
                        <span>{idea.good_lines.length} good line{idea.good_lines.length !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenIdea(idea);
                        }}
                        data-testid={`open-idea-${idea.idea_id}`}
                      >
                        Open
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => handleDeleteIdea(idea.idea_id, e)}
                        disabled={deletingId === idea.idea_id}
                        data-testid={`delete-idea-${idea.idea_id}`}
                      >
                        {deletingId === idea.idea_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* New Idea Dialog */}
      <Dialog open={isNewIdeaDialogOpen} onOpenChange={setIsNewIdeaDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider">
              New Idea
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="What's the idea? Write it down..."
              value={newIdeaText}
              onChange={(e) => setNewIdeaText(e.target.value)}
              className="min-h-[150px] font-mono text-sm"
              autoFocus
              data-testid="new-idea-textarea"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsNewIdeaDialogOpen(false);
                setNewIdeaText("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateIdea}
              disabled={!newIdeaText.trim() || isCreating}
              className="bg-primary text-primary-foreground"
              data-testid="create-idea-submit"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Create & Open
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
